/** @file This file contains utilities for highlighting code. */
import * as hljs from 'highlight.js';
import { decode } from '@codegrade/utils';
import { escape as htmlEscape } from './html';
import { visualizeWhitespace } from './visualize';
hljs.registerAliases('cu', { languageName: 'cpp' });
/**
 * Highlight a array of code.
 *
 * @param sourceArr The source array that should be highlighted.
 * @param language The language that should be used to do the highlighting.
 * @param maxLen If the array is larger than this no actual highlighting will be done.
 * @returns An array of html strings that have been escaped and should be inserted into the dom
 * like this.
 */
export function highlightCode(sourceArr, language, maxLen = 5000) {
    if (sourceArr.length > maxLen) {
        return Object.freeze(sourceArr.map(htmlEscape));
    }
    if (hljs.getLanguage(language) === undefined) {
        return Object.freeze(sourceArr.map(htmlEscape).map(visualizeWhitespace));
    }
    // Highlight.js doesn't export any of the types it uses, so we can't give `state` the
    // correct type, which should be `hljs.CompiledMode | undefined`.
    let state;
    const lastLineIdx = sourceArr.length - 1;
    const res = sourceArr.map((line, idx) => {
        const { top, value } = hljs.highlight(language, line, true, state);
        state = top;
        // Make sure that if the last line is empty we emit this as an empty
        // line. We do this to make sure that our detection for trailing
        // newlines (or actually the absence of them) works correctly.
        if (idx === lastLineIdx && line === '') {
            return visualizeWhitespace(htmlEscape(line));
        }
        return visualizeWhitespace(value);
    });
    return Object.freeze(res);
}
/**
 * Get the extension of a file from its name.
 *
 * @param name The name of the file.
 * @returns The found file extension.
 */
export function getExtension(name) {
    const fileParts = name.split('.');
    return fileParts.length > 1 ? fileParts[fileParts.length - 1] : null;
}
/**
 * Decode a buffer into a string and highlight the lines.
 *
 * @param rawBytes The buffer to decode and highlight.
 * @param filename The name of the file, used to detect the language to use for highlighting.
 * @param maxLen The maximum amount of lines before highlighting is skipped.
 * @returns The highlighted file, or an error that occurred during highlighting.
 */
export function highlightFile(rawBytes, filename, maxLen = 5000) {
    return decode.bufferE(rawBytes).map(content => {
        const lines = content.split(/\r?\n/);
        const ext = getExtension(filename) ?? 'plain';
        return highlightCode(lines, ext, maxLen);
    });
}
/** @inheritdoc */
export const listLanguages = hljs.listLanguages;
